import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { ChevronRight, Upload, AlertTriangle, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const IntervenantsImportPage = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [matchByEmail, setMatchByEmail] = useState(false);
  const [matchByCode, setMatchByCode] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  const downloadTemplate = async () => {
    try {
      const res = await fetch(`${API}/donnees-intervenants/template/excel`, { credentials: 'include' });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = 'modele_import_intervenants.xlsx'; a.click();
        window.URL.revokeObjectURL(url);
        toast.success('Modèle téléchargé');
      }
    } catch { toast.error("Erreur de téléchargement"); }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.xlsx')) { toast.error("Fichier .xlsx requis"); return; }
    setImporting(true);
    setResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('match_by_email', matchByEmail);
      formData.append('match_by_code', matchByCode);
      const res = await fetch(`${API}/donnees-intervenants/import/excel`, {
        method: 'POST', body: formData, credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setResult(data);
        toast.success(data.message);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Erreur lors de l'import");
      }
    } catch { toast.error("Erreur réseau"); }
    finally { setImporting(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
  };

  return (
    <Layout>
      <div className="space-y-6 max-w-4xl" data-testid="import-page">
        <div className="flex items-center gap-2 text-sm text-slate-400">
          <span>Données</span>
          <ChevronRight className="h-3.5 w-3.5" />
          <span className="cursor-pointer hover:text-violet-500 transition-colors" onClick={() => navigate('/donnees/intervenants')}>Intervenants</span>
          <ChevronRight className="h-3.5 w-3.5" />
          <span className="text-violet-600 font-medium">Imports</span>
        </div>

        <h1 className="text-3xl font-black text-slate-900" data-testid="page-title">Import d'intervenants</h1>

        {/* Mode d'emploi */}
        <div className="bg-white border border-slate-200 rounded-2xl p-8">
          <h2 className="text-lg font-bold text-slate-900 mb-4">Mode d'emploi</h2>
          <ul className="space-y-2 text-sm text-slate-700">
            <li>
              <span className="font-medium text-slate-900">Étape 1 -</span> Télécharger{' '}
              <button onClick={downloadTemplate} className="text-violet-600 underline hover:text-violet-800 font-medium" data-testid="download-template-link">
                ce modèle Excel pour l'import des intervenants.
              </button>
            </li>
            <li><span className="font-medium text-slate-900">Étape 2 -</span> Remplir le document</li>
            <li><span className="font-medium text-slate-900">Étape 3 -</span> Importer le fichier Excel dûment complété.</li>
          </ul>
          <div className="mt-5 bg-amber-50 border border-amber-200 rounded-xl px-5 py-4 flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
            <p className="text-sm text-amber-800 font-medium">Ne pas modifier le titre des colonnes au risque de voir l'import échouer.</p>
          </div>
        </div>

        {/* Mettre à jour */}
        <div className="bg-white border border-slate-200 rounded-2xl p-8">
          <h2 className="text-lg font-bold text-slate-900 mb-3">Mettre à jour</h2>
          <p className="text-sm text-slate-600 mb-4">L'import peut vous permettre de mettre à jour une fiche, dans ce cas l'intervenant est reconnu par :</p>
          <ul className="space-y-3 mb-5">
            <li className="flex items-center gap-3">
              <button onClick={() => setMatchByEmail(!matchByEmail)} className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${matchByEmail ? 'bg-violet-500 border-violet-500' : 'border-slate-300'}`} data-testid="match-email-checkbox">
                {matchByEmail && <span className="text-white text-xs font-bold">✓</span>}
              </button>
              <span className="text-sm text-slate-700">son email</span>
            </li>
            <li className="flex items-center gap-3">
              <button onClick={() => setMatchByCode(!matchByCode)} className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${matchByCode ? 'bg-violet-500 border-violet-500' : 'border-slate-300'}`} data-testid="match-code-checkbox">
                {matchByCode && <span className="text-white text-xs font-bold">✓</span>}
              </button>
              <span className="text-sm text-slate-700">son code interne</span>
            </li>
          </ul>
          <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-4 flex items-start gap-3 mb-6">
            <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
            <p className="text-sm text-amber-800">Toutes les colonnes seront mises à jour si vous ré-importez vos données, il faudra ainsi ré-importer les champs déjà complétés pour éviter que les informations ne soient effacées.</p>
          </div>
          <input ref={fileInputRef} type="file" accept=".xlsx" onChange={handleImport} className="hidden" data-testid="file-input" />
          <Button onClick={() => fileInputRef.current?.click()} disabled={importing} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="import-file-btn">
            <Upload className="h-4 w-4 mr-2" /> {importing ? 'Import en cours...' : 'Importer un fichier .xlsx'}
          </Button>
          {result && (
            <div className="mt-5 bg-emerald-50 border border-emerald-200 rounded-xl px-5 py-4 flex items-start gap-3" data-testid="import-result">
              <CheckCircle className="h-5 w-5 text-emerald-500 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-emerald-800">{result.message}</p>
                <p className="text-xs text-emerald-600 mt-1">{result.created} créé(s), {result.updated} mis à jour</p>
              </div>
            </div>
          )}
        </div>

        {/* À savoir */}
        <div className="bg-white border border-slate-200 rounded-2xl p-8">
          <h2 className="text-lg font-bold text-slate-900 mb-4">À savoir</h2>
          <ul className="space-y-3 text-sm text-slate-600">
            <li className="flex gap-2"><span className="text-slate-400">•</span>Il n'est pas nécessaire de remplir toutes les colonnes pour que l'import fonctionne.</li>
            <li className="flex gap-2 flex-col">
              <span>Les colonnes suivantes sont conditionnées à ces valeurs précises, ou à une case vide :</span>
              <ul className="ml-6 space-y-1 text-slate-500">
                <li>CIVILITÉ : "M" ou "Mme"</li>
                <li>STATUT : "Externe" ou "Interne"</li>
                <li>MODÈLE TARIFAIRE DE L'INTERVENANT : "Au forfait", "Par heure" ou "Par jour"</li>
                <li>LANGUE : "fr", "en", "es", "de", "it" ou "nl"</li>
              </ul>
            </li>
            <li className="flex gap-2"><span className="text-slate-400">•</span>La date de naissance doit être au format "JJ/MM/AAAA"</li>
            <li className="flex gap-2"><span className="text-slate-400">•</span>Le pays doit être identifié par son code normalisé en 2 lettres.</li>
          </ul>
        </div>

        <Button variant="outline" onClick={() => navigate('/donnees/intervenants')} data-testid="back-to-list-btn">
          Retour à la liste des intervenants
        </Button>
      </div>
    </Layout>
  );
};

export default IntervenantsImportPage;
